<template>
  <div
    v-click-outside="onClickOutside"
    class="cartridge-search-machine"
  >
    <div class="header-wrapper">
      <label class="label">
        Cartridge
      </label>
      <span
        v-if="showRequired"
        class="required-label"
      >(required)</span>
    </div>
    <CloseSvg
      v-if="((typeof(modelValue) === 'number' && modelValue > 0) || (typeof(modelValue) === 'string' && modelValue !== '' && modelValue != '0')) && showClear"
      class="close"
      @click="onClearClicked"
    />
    <TextInput
      v-if="(typeof(modelValue) === 'number' && modelValue > 0) || (typeof(modelValue) === 'string' && modelValue !== '' && modelValue != '0')"
      v-model="cartridgeName"
      :disabled="true"
    />
    <div
      v-else
      class="text-input-label"
    >
      <input
        v-model="searchTerm"
        class="text-input"
        aria-label="Search"
        :class="{'flatten-bottom': showResults}"
        placeholder="Search"
        autocomplete="off"
        :aria-required="showRequired"
        @input="onSearchTermChanged"
        @keyup.down="onDownArrowPressed"
        @keyup.up="onUpArrowPressed"
        @keyup.enter.stop="onEnterPressed"
        @keydown.tab="onClickOutside"
      >
      <div
        v-if="showResults"
        class="results-container"
      >
        <LoadingPulse
          :loading="searching"
          class="loading-spinner"
        />
        <div
          v-if="!searching"
          class="results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-option"
            :class="result.highlighted ? 'highlighted' : ''"
            @click="chooseOption(result)"
          >
            <div class="result-option-name">
              {{ result.name }}
            </div>
          </div>
          <div
            v-if="searchResults.length == 0"
            class="no-results-container"
          >
            <div class="no-results">
              No results
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { CartridgeService } from '@/services/cartridge.service'
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Toaster from '@meforma/vue-toaster'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import TextInput from '@/components/shared/TextInput.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'CartridgeSearchMachine',
  components: {
    CloseSvg,
    LoadingPulse,
    TextInput
  },
  props: {
    modelValue: {
      type: [Number, String],
      default: undefined
    },
    showClear: {
      type: Boolean,
      default: false
    },
    showRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:modelValue'
  ],
  setup() {
    const cartridgeService = inject(PROVIDER_CONSTANTS.cartridgeService) as CartridgeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      cartridgeService,
      toast
    }
  },
  data() {
    return {
      cartridges: [] as DropdownOptionResponse<number>[],
      searching: false,
      searchTerm: '',
      searchResults: [] as DropdownOptionResponse<number>[]
    }
  },
  computed: {
    cartridgeName(): string {
      let numberId = 0
      if (typeof(this.modelValue) === 'string')
        numberId = parseInt(this.modelValue)
      else
        numberId = this.modelValue

      if (numberId <= 0) return ''

      const index = this.cartridges.findIndex(c => c.id === numberId)
      if (index === -1) return ''

      return this.cartridges[index].name
    },
    showResults(): boolean {
      return this.searchTerm !== '';
    }
  },
  mounted() {
    this.cartridgeService.getCartridgeDropdownOptions().then(dropdownOptionsResponse => {
      this.cartridges = dropdownOptionsResponse.dropdownOptions
    }).catch(error => {
      if (error instanceof ErrorsResponse) {
        const errorsResponse = error as ErrorsResponse
        
        if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

        this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
      } else {
        this.toast.error('Failed to load the cartridges. Try reloading the page.')
      }
    })
  },
  methods: {
    chooseOption(cartridge: DropdownOptionResponse<number>): void {
      this.searchTerm = ''
      if (typeof(this.modelValue) === 'string')
        this.$emit('update:modelValue', cartridge.id.toString())
      else
        this.$emit('update:modelValue', cartridge.id)
    },
    getHighlightedDropdownOption(): DropdownOptionResponse<number>|undefined {
      const highlightedDropdownOptions = this.searchResults.filter(d => d.highlighted);
      return highlightedDropdownOptions.length > 0 ? highlightedDropdownOptions[0] : undefined;
    },
    onClearClicked(): void {
      if (typeof(this.modelValue) === 'string')
        this.$emit('update:modelValue', '')
      else
        this.$emit('update:modelValue', undefined)
    },
    onClickOutside(): void {
      this.searchTerm = ''
    },
    onDownArrowPressed(): void {
      const highlightedDropdownOption = this.getHighlightedDropdownOption();

      let nextIndex = highlightedDropdownOption ? this.searchResults.indexOf(highlightedDropdownOption) + 1 : 0;
      if (nextIndex > this.searchResults.length - 1)
        nextIndex = 0;
      
      if (highlightedDropdownOption)
        highlightedDropdownOption.highlighted = false;

      this.searchResults[nextIndex].highlighted = true;
    },
    onEnterPressed(): void {
      const highlightedDropdownOption = this.getHighlightedDropdownOption();
      if (highlightedDropdownOption)
        this.chooseOption(highlightedDropdownOption)
    },
    onSearchTermChanged(): void {
      if (this.searchTerm !== '') {
        this.searching = true
        this.searchCartridges()
      }
    },
    onUpArrowPressed(): void {
      const highlightedDropdownOption = this.getHighlightedDropdownOption();
      let previousIndex = highlightedDropdownOption ? this.searchResults.indexOf(highlightedDropdownOption) - 1 : 0;

      if (previousIndex < 0)
        previousIndex = this.searchResults.length - 1;

      if (highlightedDropdownOption)
        highlightedDropdownOption.highlighted = false;

      this.searchResults[previousIndex].highlighted = true;
    },
    searchCartridges(): void {
      this.searchResults = this.cartridges.filter(c => c.active && c.name.toLowerCase().indexOf(this.searchTerm) > -1)
      this.searching = false
    }
  }
})
</script>

<style scoped lang="scss">
.cartridge-search-machine {
  @include sac-search-machine;
  position: relative;

  > .close {
    position: absolute;
    right: 7px;
    top: 38px;
  }
}
</style>